import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStorageSlot } from '@gonfalon/browser-storage/useStorageSlot';
import { sidebarMetricInstrumentation } from '@gonfalon/dogfood-flags/dogfood-flags';
import { useMediaQuery } from '@gonfalon/dom/useMediaQuery';
import { useMediaQueryListener } from '@gonfalon/dom/useMediaQueryListener';
import { createStateChartReducer } from '@gonfalon/react/createStateChartReducer';
import { useHotkey } from '@gonfalon/react/useHotkey';

import { type SidebarToggleTrigger, trackSidebarSessionDuration, trackSidebarToggled } from '../analytics';
import type { KeyboardShortcut } from '../keyboardShortcuts';

export type SidebarToggleMeta = { trigger?: SidebarToggleTrigger; width?: number };

type VisibilityState = 'open' | 'closed' | 'auto-closed';

type VisibilityAction = 'toggle' | 'minimized' | 'maximized';

const computeNextVisibility = createStateChartReducer<VisibilityState, VisibilityAction>({
  open: {
    transitions: {
      toggle: 'closed',
      minimized: 'auto-closed',
    },
  },
  closed: {
    transitions: {
      toggle: 'open',
      minimized: 'closed',
      maximized: 'closed',
    },
  },
  'auto-closed': {
    transitions: {
      toggle: 'open',
      minimized: 'auto-closed',
      maximized: 'open',
    },
  },
});

type UseSidebarStateProps = {
  breakpoint: `${number}rem`;
  toggleShortcut: KeyboardShortcut;
  preferClosedByDefault?: boolean;
  storageKey?: string;
  /** Identifies this sidebar in analytics events (e.g. 'project-entity', 'main-nav'). */
  sidebarId?: string;
};

export function useSidebarState({
  breakpoint,
  toggleShortcut,
  preferClosedByDefault = false,
  storageKey,
  sidebarId,
}: UseSidebarStateProps) {
  const mediaQuery = `(max-width: ${breakpoint})`;
  const isSmall = useMediaQuery(mediaQuery);

  const persistenceEnabled = Boolean(storageKey);

  const [rawPersisted, slot] = useStorageSlot(storageKey ?? 'chrome-sidebar-noop', {
    isEnabled: persistenceEnabled,
  });

  const [visibility, setVisibility] = useState<VisibilityState>(() => {
    if (persistenceEnabled && rawPersisted === 'closed') {
      return 'closed';
    }

    if (preferClosedByDefault) {
      return 'closed';
    }

    return isSmall ? 'auto-closed' : 'open';
  });

  const isVisible = visibility === 'open';

  // Analytics: see ../analytics trackSidebar* helpers. The sidebar is a shared primitive used
  // across flags/segments/experiments, so instrumenting open/close here gives every product area
  // coverage for free (Option A in the Sidebar Metric Instrumentation proposal). `trigger` and
  // `width` are supplied by the caller via toggle(meta) since they live outside this hook.
  const openedAtRef = useRef<number | null>(isVisible ? Date.now() : null);
  const prevVisibleRef = useRef(isVisible);
  const pendingTriggerRef = useRef<SidebarToggleTrigger>('button');
  const pendingWidthRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // Gate the whole effect on the flag: when instrumentation is off this hook does no analytics
    // work at all (per review on PR #65480).
    if (!sidebarMetricInstrumentation()) {
      return;
    }
    if (prevVisibleRef.current === isVisible) {
      return;
    }
    prevVisibleRef.current = isVisible;

    const page = window.location.pathname;
    const trigger = pendingTriggerRef.current;
    const width = pendingWidthRef.current;

    if (isVisible) {
      openedAtRef.current = Date.now();
      trackSidebarToggled({ action: 'opened', trigger, page, sidebar: sidebarId, width });
    } else {
      trackSidebarToggled({ action: 'closed', trigger, page, sidebar: sidebarId, width });
      if (openedAtRef.current !== null) {
        trackSidebarSessionDuration({
          durationMs: Math.round(Date.now() - openedAtRef.current),
          page,
          sidebar: sidebarId,
        });
        openedAtRef.current = null;
      }
    }

    // Reset to defaults so a subsequent state change without explicit meta is attributed correctly.
    pendingTriggerRef.current = 'button';
    pendingWidthRef.current = undefined;
  }, [isVisible, sidebarId]);

  useMediaQueryListener(mediaQuery, (m) => {
    pendingTriggerRef.current = 'responsive';
    setVisibility((prev) => computeNextVisibility(prev, m ? 'minimized' : 'maximized'));
  });

  const updateCache = useCallback(
    (next: VisibilityState) => {
      if (!persistenceEnabled) {
        return;
      }
      if (next === 'closed') {
        slot.set('closed');
      } else {
        slot.remove();
      }
    },
    [persistenceEnabled, slot],
  );

  const toggle = useCallback(
    (meta?: SidebarToggleMeta) => {
      if (meta?.trigger) {
        pendingTriggerRef.current = meta.trigger;
      }
      if (meta?.width !== undefined) {
        pendingWidthRef.current = meta.width;
      }
      setVisibility((prev) => {
        const next = computeNextVisibility(prev, 'toggle');
        updateCache(next);
        return next;
      });
    },
    [updateCache],
  );

  useHotkey(toggleShortcut, () => toggle({ trigger: 'keyboard' }));

  return useMemo(() => ({ isVisible, toggle }), [isVisible, toggle]);
}
